import type { Topic } from '../components/layout/TopicIndex'
import type { FutureUseArea } from './siteContent'

export type ApplicationPageContent = {
  title: string
  lead: string
  intro: string[]
  highlights?: {
    title: string
    note?: string
    items: { title: string; description?: string }[]
  }
  /** What the current fictional demo covers. */
  demoScope: string
  /** How SKP, HCF and Noetivra support this domain. */
  architecture: string
  /** Proposed production uses (rendered as an indexed topic list). */
  topicsHeading: string
  topics: Topic[]
  /** Domain-specific validation and development status. */
  status: string
  futureUseArea?: FutureUseArea
  why: { title: string; body: string }
}

export const educationPage: ApplicationPageContent = {
  title: 'Education and Human Capability Development',
  lead: 'SyNeural’s knowledge infrastructure, capability frameworks and interactive workspaces are designed to help learners build reasoning and self-direction through evidence, feedback and practice.',
  intro: [
    'Through CJI and NCJI, the Human Capability Framework structures how learning tasks, human review, development activities and reassessment connect. The Semantic Knowledge Platform (SKP) links each activity to its capability definition and research basis, and Noetivra brings that work into learner and educator workspaces.',
  ],
  demoScope:
    'The current Education demo uses fictional adult learners at the Nordland Adult Learning Studio. Early-childhood and school-age applications are proposed, age-appropriate research directions, not validated current assessments.',
  architecture:
    'SKP holds the approved task versions, criteria and research references. HCF supplies the Assess → Review → Develop → Reassess cycle. Noetivra presents the learner and educator workspaces, keeping earlier submissions and feedback when a response is revised.',
  topicsHeading: 'Proposed production uses',
  topics: [
    {
      title: 'Explore the Education workflow',
      aliasIds: ['planned-noetivra-uses-in-education'],
      paragraphs: [
        'In the demo, a learner reads a task with its criteria, submits a written response and receives criterion-by-criterion feedback from an educator. A completed review can lead to a focused practice activity, which the learner completes with a reflection before a reassessment in a changed scenario.',
        'Educators can see each submission beside its criteria, assign practice and compare reassessment evidence. The emphasis is on learning and support.',
      ],
    },
    {
      title: 'Early Development and Foundational Readiness',
      paragraphs: [
        'A proposed research direction would examine how age-appropriate activities could support capabilities such as emotional regulation, attention, cooperation and basic cause-and-effect reasoning. Any work with young children would need methods designed and validated for their age, with caregivers and educators leading.',
      ],
    },
    {
      title: 'Primary and Secondary Education',
      paragraphs: [
        'A proposed school-age application could help schools strengthen reasoning quality, self-direction, media literacy, planning and consequential thinking alongside academic learning. These would be learning supports first; any assessment would need validation for school-age learners and their intended use.',
      ],
    },
    {
      title: 'Media Literacy and Manipulation Resistance',
      paragraphs: [
        'Learners face information overload, emotional framing and persuasive media. Activities could help them practise recognizing misleading framing and selective evidence while staying open to well-supported information.',
      ],
    },
    {
      title: 'Student Development Beyond Grades',
      paragraphs: [
        'Feedback tied to specific criteria could make capabilities visible that grades often miss—for example, strong potential held back by planning or confidence, or strong grades alongside weaker consequential reasoning. Pilots would examine whether this feedback helps.',
      ],
    },
    {
      title: 'Higher Education and Readiness for Responsibility',
      paragraphs: [
        'In universities and adult learning, the workflow could support critical thinking, long-range planning and preparation for professional and civic responsibility.',
      ],
    },
    {
      title: 'Support for Educators and Institutions',
      paragraphs: [
        'Institutions could review participation, support needs and progress across programs to inform curriculum design and student support. Program-level patterns would not be treated as judgments about individual learners.',
      ],
    },
  ],
  status:
    'Learning tasks and criteria in the demo are illustrative task rubrics, not validated CJI or NCJI instruments. Pilots would test whether activities and feedback lead to lasting improvement.',
  why: {
    title: 'Why It Matters for Education',
    body: 'Education shapes how people think, regulate themselves and prepare for life in institutions and society. SyNeural is designed to make that broader developmental mission more visible and more actionable, helping learners grow into more capable, resilient and responsible individuals over time.',
  },
}

export const employersPage: ApplicationPageContent = {
  title: 'Employers and Workforce Readiness',
  lead: 'SyNeural’s knowledge infrastructure, capability frameworks and interactive workspaces are designed to help employers connect role requirements to work samples, transparent review and development.',
  intro: [
    'Through CJI and NCJI, the Human Capability Framework structures review and development. SKP maintains a consistent language for skills and role requirements, and Noetivra Talent brings work-sample review and development into candidate and reviewer workspaces.',
  ],
  demoScope:
    'The current Talent demo uses a fictional Project Coordinator role at Meridian Projects and two fictional candidates. Reviewers compare work samples against the same criteria; the demo does not rank candidates.',
  architecture:
    'SKP holds approved role requirements, questions and rubrics with their versions. HCF supplies the review and development cycle. Noetivra presents candidate and reviewer workspaces and keeps human decisions in a separate record from development findings.',
  topicsHeading: 'Proposed production uses',
  topics: [
    {
      title: 'Structured Interviews and Candidate Review',
      paragraphs: [
        'In the demo, each role requirement connects to a task, and reviewers record observations, explanations and unanswered questions against shared criteria. Comparison views show the same criteria for each candidate with links to every record.',
        'A separate human-decision form—request follow-up, advance or do not advance—requires reviewed evidence and a written reason. Human reviewers make hiring decisions. Hiring assessments would require validation for their intended use, accessibility options and testing for unequal outcomes.',
      ],
    },
    {
      title: 'Workforce Development and Role Readiness',
      paragraphs: [
        'A production deployment could help employers understand non-technical capabilities that shape role readiness, such as planning, communication and consequential thinking, and connect them to development.',
      ],
    },
    {
      title: 'Advancement and Internal Mobility',
      paragraphs: [
        'Development pathways could help employees prepare for greater responsibility. Opportunity guidance would remain separate from consequential eligibility decisions, which stay with authorized people.',
      ],
    },
    {
      title: 'Execution, Communication, and Team Performance',
      paragraphs: [
        'Many bottlenecks come from planning, communication or judgment under pressure rather than technical skill. Practice activities could target these, and a pilot would examine whether they improve outcomes.',
      ],
    },
    {
      title: 'Organizational Resilience',
      paragraphs: [
        'Organizations could use aggregate development data for succession planning and capability-building. Group patterns would not establish any individual’s capabilities, and outcomes also depend on resources, incentives and working conditions.',
      ],
    },
  ],
  status:
    'Talent criteria in the demo are illustrative task rubrics. Before production use in hiring, methods would need evidence that they are fit for the specific role, accessible and fair across groups.',
  futureUseArea: 'talent',
  why: {
    title: 'Why It Matters for Employers',
    body: 'Employers need people who can turn skill into judgment and judgment into reliable contribution. SyNeural is designed to make the evidence behind those decisions inspectable while keeping hiring decisions with people.',
  },
}

export const civilSocietyPage: ApplicationPageContent = {
  title: 'Civil Society and Community Capability',
  lead: 'SyNeural’s knowledge infrastructure, capability frameworks and interactive workspaces are designed to help community organizations connect personal goals, mentorship and practical support through respectful participation.',
  intro: [
    'Through CJI and NCJI, the Human Capability Framework structures development and review. SKP can connect community knowledge held in scattered reports and programs, and Noetivra for Communities brings participant and program-team workspaces together while each organization keeps control of its records.',
  ],
  demoScope:
    'The current Community demo uses the fictional Harbor Community Network and two partner organizations. Referrals require explicit participant consent and share only the summary the participant agreed to; no message or record is sent to another organization.',
  architecture:
    'SKP connects program knowledge and resources. HCF supplies the shared development cycle. Noetivra presents participant and program-team workspaces and records purpose-limited referrals with their status history.',
  topicsHeading: 'Proposed production uses',
  topics: [
    {
      title: 'Consent-Based Partner Referrals',
      paragraphs: [
        'In the demo, a program team records the participant, receiving organization, purpose and exact information to share, with confirmation that the participant agreed. Each status change requires a reason and appears in the participant’s view.',
        'Community organizations can participate in shared programs without receiving unrestricted access to government or partner records. A referral never grants access to a participant’s complete record.',
      ],
    },
    {
      title: 'Community Knowledge and Collaborative Program Review',
      paragraphs: [
        'Organizations often hold valuable knowledge in disconnected reports and evaluations. SKP could connect that knowledge so teams can find what has been tried and what was learned, and review programs together while each organization controls what it shares.',
      ],
    },
    {
      title: 'Mentorship and Upward Mobility',
      paragraphs: [
        'Development plans could connect a participant’s goals and barriers with mentoring, training and community support, with the participant agreeing each step.',
      ],
    },
    {
      title: 'Misinformation Resistance and Civic Participation',
      paragraphs: [
        'Programs could offer practice in recognizing misleading framing and in constructive participation. A pilot would examine whether such practice makes a lasting difference.',
      ],
    },
    {
      title: 'Support Systems and Opportunity Alignment',
      paragraphs: [
        'Aggregate views could show where support is missing, such as reported barriers to participation and access to support. Group patterns describe programs and communities, not individuals.',
      ],
    },
  ],
  status:
    'Community criteria in the demo are illustrative task rubrics. Production use would require data-sharing agreements between organizations and evaluation of whether programs help participants.',
  why: {
    title: 'Why It Matters for Civil Society',
    body: 'Much of the real work of development, belonging and support takes place in civil society. SyNeural is designed to help those organizations coordinate support with clear consent and boundaries, and to learn which programs help.',
  },
}

/** Proposed NCDS public-service and program improvement cycle. */
export const ncdsCycle = [
  'Identify needs',
  'Establish a baseline',
  'Agree a support plan',
  'Deliver support',
  'Measure outcomes',
  'Improve delivery',
]

export const governmentPage: ApplicationPageContent = {
  title: 'Government and Public Sector Applications',
  lead: 'SyNeural’s knowledge infrastructure, capability frameworks and interactive workspaces are designed to help public institutions connect individual support with program delivery and accountable institutional review.',
  intro: [
    'Through CJI and NCJI, the Human Capability Framework structures assessment and development where they are appropriate. SKP can organize policies, program knowledge and role requirements, and Noetivra provides separate citizen and administration workspaces. Official decisions, service delivery, identity, case management and appeals remain with the relevant operational systems and the people responsible for them.',
  ],
  highlights: {
    title: 'Three application scales',
    note: 'These groupings describe where the solution can be applied. The fictional Nordland demonstration uses a more detailed navigation hierarchy: National → Region → State → Province → City → District. A real implementation would follow the participating country’s administrative structure.',
    items: [
      { title: 'Municipal & Local', description: 'Community support patterns and local development' },
      { title: 'Regional & Provincial', description: 'Broader planning across education, labor and resilience' },
      { title: 'National Government', description: 'Ministry-level planning and civil-service modernization' },
    ],
  },
  demoScope:
    'The current Government demo uses the fictional Republic of Nordland. It separates the Citizen Workspace from the Administration Control Panel; the workspace switch demonstrates workflows and is not a secure account system.',
  architecture:
    'SKP holds approved policies, program knowledge and their versions. HCF supplies development methods where they apply. Noetivra presents the citizen and administration workspaces. Connected operational systems would handle identity, case management, permissions and authorized actions.',
  topicsHeading: 'Proposed production uses',
  topics: [
    {
      title: 'Municipal and Local Government',
      paragraphs: [
        'Local institutions could use aggregate views of reported needs and service access to plan youth programs, workforce initiatives and community development. Group patterns would not establish any individual’s capabilities.',
      ],
    },
    {
      title: 'Regional and Provincial Government',
      paragraphs: [
        'Regional planners could compare support needs and program outcomes across districts to target education, workforce and institutional strengthening.',
      ],
    },
    {
      title: 'National Government',
      paragraphs: [
        'At national level, NCDS could connect ministry planning, civil-service development and program evaluation. Institutional outcomes also depend on resources, incentives, services and conditions, not individual judgment alone.',
      ],
    },
    {
      title: 'Policy Exploration, Staff Learning, and Authorized Review',
      paragraphs: [
        'SKP could organize approved policies, program knowledge and rules with their sources and versions, so staff can find current guidance. Staff could follow learning activities tied to their roles and participate in authorized review workflows.',
        'Noetivra could provide an authorized interface to operational workflows; it would not replace them.',
      ],
    },
    {
      title: 'Institutional Performance',
      paragraphs: [
        'Service delays, access barriers, administrative errors and unresolved requests would be visible alongside program outcomes, so institutions improve in the same cycle as the people they serve.',
      ],
    },
    {
      title: 'Public Workforce and Leadership Readiness',
      paragraphs: [
        'Public-sector training and succession planning could use the shared development workflow. Role qualification is a separate proposed use that would require evidence for the particular role and human review.',
      ],
    },
  ],
  status:
    'NCDS is a proposed government implementation. Any assessment used in public services would need validation for that use, and decisions affecting people would require notice, human review and appeal.',
  futureUseArea: 'government',
  why: {
    title: 'Why It Matters for Government',
    body: 'Public problems are often connected to human capability, service access and institutional performance at once. NCDS is designed to make all three visible in one improvement cycle, with accountable people making the decisions.',
  },
}

export const leadershipPage: ApplicationPageContent = {
  title: 'Leadership Development and Readiness',
  lead: 'SyNeural’s knowledge infrastructure, capability frameworks and interactive workspaces are designed to make consequential decisions inspectable, then connect feedback to coached practice.',
  intro: [
    'Leadership is a reusable capability: the same exercises can be configured within employer, government, education and community programs. SKP connects leadership frameworks to their definitions and research, HCF supplies the development cycle, and Noetivra Leadership brings participant and coach workspaces together.',
  ],
  highlights: {
    title: 'Capabilities the exercises focus on',
    items: [
      { title: 'Reasoning Under Pressure' },
      { title: 'Consequence Awareness' },
      { title: 'Emotional Stability' },
      { title: 'Communication Clarity' },
      { title: 'Systems Thinking' },
      { title: 'Responsibility at Scale' },
    ],
  },
  demoScope:
    'The current Leadership demo is a coaching demonstration at the fictional Institutional Leadership Lab: a participant works through a service-outage decision, receives coach feedback, rehearses a decision briefing and revisits the decision when the situation changes.',
  architecture:
    'SKP holds the scenarios, criteria and leadership frameworks with their versions. HCF supplies the Assess → Review → Develop → Reassess cycle. Noetivra presents the participant and coach workspaces and shows both rounds side by side.',
  topicsHeading: 'Proposed production uses',
  topics: [
    {
      title: 'Scenario Exercises, Decision Reviews, and Progress Views',
      paragraphs: [
        'Leaders could work through scenario exercises and crisis simulations, then review their decisions alongside the reasoning, trade-offs and consequences involved. Progress views would show change across reassessments without an overall score.',
      ],
    },
    {
      title: 'Leadership Development Pathways',
      paragraphs: [
        'Development gaps would define what stronger readiness requires and how it can be built, through coaching, training, mentorship and structured experience, rather than simply disqualifying a person.',
      ],
    },
    {
      title: 'Leadership Pipelines and Succession',
      paragraphs: [
        'Organizations could use development records to plan succession and advancement. A pilot would examine whether the exercises predict anything about later performance.',
      ],
    },
    {
      title: 'Executive and High-Responsibility Environments',
      paragraphs: [
        'In senior roles, exercises could focus on consequence reasoning, crisis stability and communication under pressure.',
        'Role qualification is a separate proposed use requiring evidence for the particular role, an explanation of findings and a meaningful route to human review and reassessment.',
      ],
    },
  ],
  status:
    'Leadership scenarios and criteria in the demo are illustrative. Task performance is not a validated measure of leadership readiness.',
  futureUseArea: 'leadership',
  why: {
    title: 'Why It Matters for Leadership Development',
    body: 'Leadership judgment scales through institutions and communities. SyNeural is designed to make that judgment inspectable and developable, so leaders are prepared to carry complexity and consequence over time.',
  },
}
